/**
 * Grid render timing utilities.
 *
 * Captures the createGrid → onFirstDataRendered elapsed time and provides
 * a dev-gated logger and an injectable reporting seam for downstream
 * performance reporters (e.g. a Sentry span emitter).
 */

/**
 * Context associated with a grid render timing measurement.
 * Consumers add dimensions for display, filtering, and aggregation.
 */
export type GridRenderContext = {
  /** Grid component identifier, derived from the element's data-component attr. */
  readonly component: string
  /** Leaf-column count at render time — useful for per-width analysis. */
  readonly columnCount?: number
  /** AG Grid row model type in use. */
  readonly rowModelType?: 'clientSide' | 'serverSide'
}

/**
 * Reporter function signature consumed by the reporting seam.
 * A Sentry span emitter or any other observer implements this shape.
 */
export type GridRenderReporter = (
  durationMs: number,
  context: GridRenderContext
) => void

// Module-level slot for the active reporter (null = no-op).
// Kept as a mutable slot here rather than a closure so the seam is
// replaceable without re-importing the whole module.
let _reporter: GridRenderReporter | null = null

// ---------------------------------------------------------------------------
// Pure timing helper
// ---------------------------------------------------------------------------

/**
 * Compute the elapsed time between two performance.now() timestamps.
 *
 * Pure function: timestamps in → duration in ms out. No side effects.
 */
export const measureGridRender = (startMs: number, endMs: number): number =>
  endMs - startMs

// ---------------------------------------------------------------------------
// Dev-gated structured logger
// ---------------------------------------------------------------------------

/**
 * Emit a structured console.debug entry for a completed grid render.
 *
 * No-op in production (`process.env.NODE_ENV === 'production'`).
 * All arguments are value-typed so the call is tree-shakeable in prod builds.
 */
export const logGridRenderTiming = (
  durationMs: number,
  context: GridRenderContext
): void => {
  if (process.env.NODE_ENV === 'production') {
    return
  }

  // biome-ignore lint/suspicious/noConsole: intentional dev-only debug log, production gate above
  console.debug('[grid render]', { durationMs, ...context })
}

// ---------------------------------------------------------------------------
// Reporting seam
// ---------------------------------------------------------------------------

/**
 * Register the active grid render reporter.
 *
 * Pass `null` to reset to the default no-op (useful in tests).
 * The reporter is called from `onFirstDataRendered` with the measured duration
 * and grid context; a Sentry span emitter plugs in here.
 */
export const setGridRenderReporter = (
  reporter: GridRenderReporter | null
): void => {
  _reporter = reporter
}

/**
 * Hand the measured duration and context to the registered reporter.
 *
 * No-op when no reporter has been registered.
 * This is the stable seam between timing capture and telemetry emission.
 */
export const reportGridRender = (
  durationMs: number,
  context: GridRenderContext
): void => {
  _reporter?.(durationMs, context)
}
